/**
 * Web: SHXRE clip video with ref-based play when isActive (autoplay policy).
 * Starts unmuted; if the browser blocks unmuted autoplay, falls back to muted and tap unmutes.
 * Tap to pause/play. Records view at 10% watched.
 * Native: not used (parent uses StudioClipNativeVideo).
 */
import {useEffect, useRef, useState} from 'react'
import {Platform, Pressable, View} from 'react-native'

import {studioRecordView} from '#/lib/api/studio'

export function StudioClipVideoWeb({
  src,
  poster,
  isActive,
  paused,
  onTap,
  videoId,
}: {
  src: string
  poster: string | undefined
  isActive: boolean
  paused: boolean
  onTap: () => void
  videoId?: string
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [muted, setMuted] = useState(true)
  const [isLoaded, setIsLoaded] = useState(false)
  const viewRecordedRef = useRef(false)

  useEffect(() => {
    if (Platform.OS !== 'web') return
    const el = videoRef.current
    if (!el) return
    if (!isActive) {
      el.pause()
      return
    }
    if (paused) {
      el.pause()
    } else {
      const p = el.play()
      if (p?.then) {
        p.catch(() => {
          setMuted(true)
          el.muted = true
          el.play().catch(() => {})
        })
      }
    }
  }, [isActive, paused])

  useEffect(() => {
    if (!isActive) viewRecordedRef.current = false
  }, [isActive])

  if (Platform.OS !== 'web') return null
  return (
    <Pressable
      onPress={() => {
        setMuted(false)
        onTap()
      }}
      style={{flex: 1, width: '100%', height: '100%', backgroundColor: '#000'}}
      accessibilityRole="button"
      accessibilityLabel="Tap to pause or play">
      {!isLoaded && (
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#111',
            zIndex: 1,
          }}
        />
      )}
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        playsInline
        muted={muted}
        loop
        preload="auto"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
        onLoadedData={() => setIsLoaded(true)}
        onTimeUpdate={() => {
          const el = videoRef.current
          if (!el || !videoId || viewRecordedRef.current || el.duration <= 0) return
          if (el.currentTime >= el.duration * 0.1) {
            viewRecordedRef.current = true
            studioRecordView(videoId).catch(() => {})
          }
        }}
      />
    </Pressable>
  )
}
